// src/auth/Login.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import useAuthStore from '../stores/authStore';
import axios from 'axios';
import './Login.css';

const API_URL = import.meta.env.VITE_API_URL;

// ===== ユーティリティ =====
const base64EncodeJson = (obj) => {
  try {
    return btoa(unescape(encodeURIComponent(JSON.stringify(obj))));
  } catch {
    return '';
  }
};

const resolveRedirectPath = (WuserType, nextParam) => {
  const safeNext = nextParam && nextParam.startsWith('/') ? nextParam : null;

  if (safeNext && safeNext !== '/') return safeNext;

  if (WuserType === 'system') return '/enduser/usersettings';
  if (WuserType === 'admin') return '/enduser/usersettings';
  if (WuserType === 'operator') return '/enduser/usersettings';

  return '/enduser/usersettings';
};

// ===== コンポーネント =====
const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, logout } = useAuthStore();

  const lastId = localStorage.getItem('lastLoginID') || '';

  const [userAddress, setUserAddress] = useState(lastId);
  const [userPass, setUserPass] = useState('');
  const [remember, setRemember] = useState(!!lastId);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  // ---- 招待リンクURL（オリジンは現在のURLから取得） ----
  // ---- 本番のトークンになっている ----
  const INVITE_TOKEN = '96b29b94e46ae6447ce8a9e858e9c2282a4c6831c9c0d324da969084e4afd797';
  const inviteUrl = `${window.location.origin}/userinvite?t=${INVITE_TOKEN}`;

  const canSubmit =
    userAddress.trim().length > 0 &&
    userPass.trim().length > 0 &&
    !submitting;

  // ログインID記憶
  useEffect(() => {
    if (remember && userAddress.trim()) {
      localStorage.setItem('lastLoginID', userAddress.trim());
    } else {
      localStorage.removeItem('lastLoginID');
    }
  }, [remember, userAddress]);

  const getNextPath = () => {
    const params = new URLSearchParams(location.search);
    const next = params.get('next');
    return next && next.startsWith('/') ? next : null;
  };

  // ---- API エラーハンドリング ----
  const handleApiError = (error) => {
    console.error('status', error.response?.status);
    console.error('data', error.response?.data);

    if (error.response?.status === 429) {
      const retry = parseInt(error.response?.headers?.['retry-after'] ?? '10', 10);
      setServerError(`操作が集中しています。${retry}秒後に再試行できます。`);
    } else if (error.response?.status === 400) {
      setServerError('メールアドレス・パスワードをご確認ください。');
    } else if (error.message === 'Network Error') {
      setServerError('ネットワークエラーが発生しました。時間をおいて再度お試しください。');
    } else {
      setServerError('ログイン中にエラーが発生しました。再度お試しください。');
    }
  };

  // ---- フォーム送信 ----
  const handleLogin = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setServerError('');

    try {
      const res = await axios.post(`${API_URL}/api/auth/login/`, {
        userAddress,
        userPass,
      });

      if (res.data?.success) {
        const { WuserName, WuserType, access, refresh } = res.data;

        login({ access, refresh, WuserName, WuserType });

        const nextParam = getNextPath();
        const redirectTo = resolveRedirectPath(WuserType, nextParam);
        navigate(redirectTo, { replace: true });
      } else {
        setServerError('メールアドレス・パスワードをご確認ください。');
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  // ---- OAuth ----
  const startOAuth = (provider) => {
    const statePayload = {
      next: getNextPath() ?? '/',
      ts: Date.now(),
      nonce: Math.random().toString(36).slice(2),
    };
    const state = base64EncodeJson(statePayload);

    sessionStorage.setItem('oauth_state', state);

    const url = `${API_URL}/api/auth/login/${provider}/start?state=${encodeURIComponent(state)}`;
    window.location.href = url;
  };

  // ---- OAuth コールバック処理 ----
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const from = params.get('from');

    if (from !== 'oauth') return;

    const access = params.get('access');
    const refresh = params.get('refresh');
    const WuserName = params.get('name');
    const WuserType = params.get('type');
    const next = params.get('next');

    if (!access || !refresh) {
      logout();
      navigate('/login?err=oauth', { replace: true });
      return;
    }

    login({ access, refresh, WuserName, WuserType });

    const redirectTo = resolveRedirectPath(WuserType, next);
    navigate(redirectTo, { replace: true });
  }, [location, navigate, login, logout]);

  return (
    <div className="login-container">
      <div className="login-card" role="dialog" aria-labelledby="login-title">
        <h2 id="login-title" className="login-title">ログイン</h2>

        {serverError && (
          <div className="error-alert" role="alert">
            {serverError}
          </div>
        )}

        <form onSubmit={handleLogin} noValidate>
          {/* メールアドレス */}
          <div className="form-group">
            <label htmlFor="userAddress">メールアドレス</label>
            <input
              id="userAddress"
              name="userAddress"
              type="email"
              autoComplete="username"
              value={userAddress}
              onChange={(e) => setUserAddress(e.target.value)}
              placeholder="your@example.com"
              required
              aria-invalid={!!serverError}
            />
          </div>

          {/* パスワード */}
          <div className="form-group">
            <label htmlFor="userPass">パスワード</label>
            <div className="password-field">
              <input
                id="userPass"
                name="current-password"
                type={isPasswordVisible ? 'text' : 'password'}
                autoComplete="current-password"
                value={userPass}
                onChange={(e) => setUserPass(e.target.value)}
                placeholder="パスワード"
                required
                aria-invalid={!!serverError}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setIsPasswordVisible((v) => !v)}
                aria-label={isPasswordVisible ? 'パスワードを非表示' : 'パスワードを表示'}
              >
                {isPasswordVisible ? '隠す' : '表示'}
              </button>
            </div>
          </div>

          {/* オプション行 */}
          <div className="form-row">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              ログインIDを記憶する
            </label>
            <Link className="link" to="/password-reset-request">
              パスワードをお忘れの方
            </Link>
          </div>

          <button
            type="submit"
            className={`login-button ${submitting ? 'is-loading' : ''}`}
            disabled={!canSubmit}
          >
            {submitting ? 'ログイン中…' : 'ログイン'}
          </button>
        </form>

        {/* OAuth */}
        {/* <div className="oauth-divider">
          <span>または</span>
        </div>
        <div className="oauth-buttons">
          <button
            type="button"
            className="oauth-btn oauth-google"
            onClick={() => startOAuth('google')}
            aria-label="Googleでログイン"
          >
            Googleでログイン
          </button>
          <button
            type="button"
            className="oauth-btn oauth-line"
            onClick={() => startOAuth('line')}
            aria-label="LINEでログイン"
          >
            LINEでログイン
          </button>
        </div>
        <div style={{ color: '#888', marginTop: 10, textAlign: 'center', fontSize: 13 }}>
          <span>GoogleまたはLINEアカウントにて新規ログインの場合、</span>
        </div>
        <div style={{ color: '#888', textAlign: 'center', fontSize: 13 }}>
          <span>メールアドレスを利用しアカウントを作成いたします</span>
        </div>

        <div style={{ marginTop: 10, textAlign: 'center', fontSize: 13 }}>
          アカウントをお持ちでない方は <Link to="/signup">新規登録</Link>
        </div> */}

        <div style={{ marginTop: 10, textAlign: 'center', fontSize: 13 }}>
          アカウントをお持ちでない方は{' '}
          <a className="link" href={inviteUrl}>
            新規登録
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;